import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// MUI Icons
import ImageOutlinedIcon         from '@mui/icons-material/ImageOutlined';
import GridViewOutlinedIcon      from '@mui/icons-material/GridViewOutlined';
import ArrowForwardIcon          from '@mui/icons-material/ArrowForward';
import CropFreeIcon              from '@mui/icons-material/CropFree';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import GavelOutlinedIcon         from '@mui/icons-material/GavelOutlined';
import AssignmentOutlinedIcon    from '@mui/icons-material/AssignmentOutlined';
import LockOutlinedIcon          from '@mui/icons-material/LockOutlined';
import LogoutIcon                from '@mui/icons-material/Logout';
import EditOutlinedIcon          from '@mui/icons-material/EditOutlined';
import WeekendOutlinedIcon       from '@mui/icons-material/WeekendOutlined';
import FormatPaintOutlinedIcon   from '@mui/icons-material/FormatPaintOutlined';

// Page components
import SpacePlanning          from '../components/SpacePlanning/index2.jsx';
import Moodboarding           from '../components/Moodboard/Moodboarding4.jsx';
import MaterialsShortlist     from '../components/Moodboard/MaterialShortlist4.jsx';
import FurnitureShortlist     from '../components/Moodboard/FurnitureShortlist6.jsx';
import RequestSamples         from '../components/Moodboard/RequestSamples2.jsx';
import BidManagement          from '../components/BidManagement2.jsx';
import Questionnaire          from '../components/Questionnaire5.jsx';
import AccountPage            from '../components/AccountPage.jsx';

import { apiFetch } from '../../api/apiClient';
import { clearToken } from '../../auth/token';
import { useToast } from '../../shared/components/status';

import { GREEN, CHARCOAL, BORDER, MUTED, MUTED_FAINT, STONE50,
    labelMono, bodyFont, appGridBg } from '../../theme/tokens2';

// Change this one line when you release a new version
const APP_VERSION = 'v1.0.0';

// Shared focus style: no box after a mouse click, green outline for keyboard users
const focusStyles = {
  '&:focus': { outline: 'none' },
  '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '-2px' },
};


// ─── Zone 1: Build Your Brief ─────────────────────────────────────────────────
const BRIEF_TOOLS = [
  {
    id: 'SpacePlanning',
    Icon: CropFreeIcon,
    label: 'Space Planning',
    description: 'Visualise your floor plan and explore practical layout options.',
  },
  {
    id: 'Moodboarding',
    Icon: ImageOutlinedIcon,
    label: 'Moodboard',
    description: "Share images that inspire you. We match them to real materials and furniture in the market.",
  },
  {
    id: 'MaterialsShortlist',
    Icon: FormatPaintOutlinedIcon,
    label: 'Materials',
    description: 'Compare your options side by side and rank your favorites. Then shortlist the ones you love, and we’ll send free samples to your door.',
  },
  {
    id: 'FurnitureShortlist',
    Icon: WeekendOutlinedIcon,
    label: 'Fittings & Furnishings',
    description: 'All your furniture, bathroom fittings, and appliance options, in one place. Rank your favorites so choosing feels easy, not overwhelming.',
  },
  {
    id: 'Questionnaire',
    Icon: AssignmentOutlinedIcon,
    label: 'Questionnaire',
    description: 'Help your designer understand how you live. Your answers give them the context they need to propose spaces that truly fit your lifestyle.',
  },
];

// ─── Zone 2: From Plan to Reality ────────────────────────────────────────────
const ACTION_TOOLS = [
  {
    id: 'RequestSamples',
    Icon: LocalShippingOutlinedIcon,
    label: 'Sample Delivery',
    description: "Request samples of materials you shortlisted, and we will source them. Where available, we will send the samples to your home so you can see and feel them before deciding.",
  },
  {
    id: 'BidManagement',
    Icon: GavelOutlinedIcon,
    label: 'Bid Management',
    description: 'When your brief is ready, trigger this to send your inputs to our network of designers. Receive structured, comparable quotes — no more apples-to-oranges pricing.',
  },
];

// ─── renderContent ────────────────────────────────────────────────────────────
function renderContent(activeTool, setActiveTool) {
  switch (activeTool) {
    case 'SpacePlanning':      return <SpacePlanning />;
    case 'Moodboarding':       return <Moodboarding />;
    case 'MaterialsShortlist': return (
      <MaterialsShortlist onRequestSamples={() => setActiveTool('RequestSamples')} />
    );
    case 'FurnitureShortlist': return <FurnitureShortlist />;
    case 'RequestSamples':     return <RequestSamples onBackToMaterials={() => setActiveTool('MaterialsShortlist')} />;
    case 'BidManagement':      return <BidManagement />;
    case 'Questionnaire':      return <Questionnaire />;
    case 'AccountPage':        return <AccountPage />;
    default:                   return null;
  }
}

// ─── ProjectNameField ─────────────────────────────────────────────────────────
// The project name at the top right. Click it to rename.
function ProjectNameField() {
  const toast = useToast();
  const [name,    setName]    = useState('');
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState('');
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    apiFetch('/api/householdprofile/my')
      .then((data) => setName(data?.projectname || ''))
      .catch(() => { /* keep the default name; nothing else depends on it */ })
      .finally(() => setLoading(false));
  }, []);

  const beginEdit = () => {
    setDraft(name);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitEdit = async () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed === name) return;
    const previous = name;
    setName(trimmed); // show the new name straight away
    try {
      await apiFetch('/api/householdprofile/projectname', { method: 'PATCH', body: { projectname: trimmed } });
    } catch (err) {
      setName(previous); // put the old name back
      toast.error("We couldn't rename your project. Please try again.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter')  commitEdit();
    if (e.key === 'Escape') { setEditing(false); setDraft(name); }
  };

  const displayName = name || 'My Renovation';

  if (loading) return null;

  if (editing) {
    return (
      <Box
        component="input"
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        maxLength={48}
        placeholder="Name your project..."
        aria-label="Project name"
        sx={{
          border: 'none', borderBottom: `1px solid ${GREEN}`,
          outline: 'none', background: 'transparent',
          fontSize: '0.875rem', fontFamily: 'inherit',
          color: CHARCOAL, letterSpacing: '0.01em',
          width: 220, maxWidth: '100%', py: '2px',
          '&::placeholder': { color: 'rgba(0,0,0,0.25)' },
        }}
      />
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={beginEdit}
      title="Rename your project"
      aria-label={`Project name: ${displayName}. Click to rename.`}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1, py: 0.5, mx: -1,           // mx: -1 keeps the text in line with its neighbours
        minWidth: 0,
        background: 'none', border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': { backgroundColor: STONE50 },
        '&:hover .edit-hint': { opacity: 1 },
        ...focusStyles,
      }}
    >
      <Typography component="span" sx={{
        fontSize: '0.875rem', letterSpacing: '0.01em',
        color: CHARCOAL, fontWeight: 500,
        fontFamily: 'inherit', userSelect: 'none',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {displayName}
      </Typography>
      <EditOutlinedIcon className="edit-hint" sx={{
        fontSize: '0.85rem', color: MUTED, flexShrink: 0,
        opacity: 0.45, transition: 'opacity 0.15s ease',
      }} />
    </Box>
  );
}

// ─── AccountPopover ───────────────────────────────────────────────────────────
function AccountPopover({ onNavigate, isActive }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const highlighted = open || isActive;
  const ref = useRef(null);

  // Close when clicking outside, or pressing Esc
  useEffect(() => {
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const handleLogout = () => {
    clearToken();
    navigate('/', { replace: true });
  };

  return (
    <Box ref={ref} sx={{ position: 'relative', flexShrink: 0 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Account"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{
          width: 30, height: 30,
          backgroundColor: highlighted ? 'rgba(33,131,51,0.08)' : 'transparent',
          border: `1.5px solid ${highlighted ? GREEN : 'rgba(0,0,0,0.18)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
          '&:hover': { borderColor: 'rgba(33,131,51,0.30)', backgroundColor: 'rgba(33,131,51,0.06)' },
          '&:focus': { outline: 'none' },
          '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden
          stroke={highlighted ? GREEN : MUTED} strokeWidth="1.8">
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
        </svg>
      </Box>

      {open && (
        <Box role="menu" sx={{
          position: 'absolute', bottom: '100%', left: 0, mb: 1,
          width: 192, backgroundColor: '#fff',
          border: `1px solid ${BORDER}`,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          zIndex: 50,
        }}>
          <PopoverButton
            icon={<LockOutlinedIcon sx={{ fontSize: '0.875rem' }} />}
            color={MUTED}
            onClick={() => { onNavigate('AccountPage'); setOpen(false); }}
          >
            My Account
          </PopoverButton>
          <Box sx={{ height: '1px', backgroundColor: BORDER, mx: 1.5 }} />
          <PopoverButton
            icon={<LogoutIcon sx={{ fontSize: '0.875rem' }} />}
            color="#B42318"
            hoverBg="rgba(180,35,24,0.05)"
            onClick={handleLogout}
          >
            Log Out
          </PopoverButton>
        </Box>
      )}
    </Box>
  );
}

function PopoverButton({ icon, children, color, hoverBg, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      role="menuitem"
      onClick={onClick}
      sx={{
        width: '100%', background: 'none', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 1.5, py: 1.25,
        backgroundColor: 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': { backgroundColor: hoverBg ?? STONE50 },
        ...focusStyles,
      }}
    >
      <Box sx={{ color, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Typography component="span" sx={{ fontSize: '0.8125rem', color, ...bodyFont }}>{children}</Typography>
    </Box>
  );
}

// ─── ToolCard ─────────────────────────────────────────────────────────────────
function ToolCard({ tool }) {
  const [hovered, setHovered] = useState(false);
  const { Icon } = tool;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => tool.onSelect(tool.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative', textAlign: 'left',
        backgroundColor: '#fff',
        border: `1.5px solid ${hovered ? GREEN : 'rgba(0,0,0,0.14)'}`,
        p: '20px',
        display: 'flex', flexDirection: 'column',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.10)' : 'none',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        cursor: 'pointer',
        '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
      }}
    >
      {/* Label + icon on same row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5, width: '100%' }}>
        <Typography component="span" sx={{
          fontSize: '1rem', fontWeight: 600, color: CHARCOAL,
          letterSpacing: '-0.01em', ...bodyFont,
        }}>
          {tool.label}
        </Typography>
        <Box aria-hidden sx={{
          width: 34, height: 34, flexShrink: 0,
          border: `1.5px solid ${hovered ? GREEN : 'rgba(33,131,51,0.35)'}`,
          backgroundColor: hovered ? 'rgba(33,131,51,0.08)' : 'rgba(33,131,51,0.04)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
        }}>
          <Icon sx={{ fontSize: '0.95rem', color: GREEN }} />
        </Box>
      </Box>

      {/* Hairline rule */}
      <Box aria-hidden sx={{
        height: '2px',
        width: hovered ? 36 : 20,
        backgroundColor: hovered ? GREEN : 'rgba(0,0,0,0.18)',
        mb: 1.5,
        transition: 'width 0.25s ease, background-color 0.25s ease',
      }} />

      {/* Description */}
      <Typography component="span" sx={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.72)', lineHeight: 1.6, flex: 1, ...bodyFont }}>
        {tool.description}
      </Typography>

      {/* Open */}
      <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5 }}>
        <Typography component="span" sx={{ ...labelMono, color: GREEN, fontSize: '9px' }}>Open</Typography>
        <ArrowForwardIcon sx={{ fontSize: '0.75rem', color: GREEN }} />
      </Box>
    </Box>
  );
}

// ─── SidebarItem ──────────────────────────────────────────────────────────────
function SidebarItem({ tool, isActive, onClick }) {
  const { Icon } = tool;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2.5, py: 1,
        background: 'none', border: 'none',
        borderLeft: `2px solid ${isActive ? GREEN : 'transparent'}`,
        backgroundColor: isActive ? 'rgba(33,131,51,0.06)' : 'transparent',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        textAlign: 'left',
        '&:hover': { backgroundColor: isActive ? 'rgba(33,131,51,0.06)' : STONE50 },
        ...focusStyles,
      }}
    >
      <Icon sx={{ fontSize: '1.05rem', flexShrink: 0, color: isActive ? GREEN : CHARCOAL, opacity: isActive ? 1 : 0.55 }} />
      <Typography component="span" sx={{
        ...bodyFont,
        fontSize: '0.85rem',
        fontWeight: isActive ? 600 : 500,
        letterSpacing: '0.005em',
        textTransform: 'none',
        flex: 1,
        color: isActive ? GREEN : CHARCOAL,
        opacity: isActive ? 1 : 0.72,
      }}>
        {tool.label}
      </Typography>
    </Box>
  );
}

// ─── ZoneHeader ───────────────────────────────────────────────────────────────
function ZoneHeader({ label, description }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: description ? 1 : 0 }}>
        <Typography component="h2" sx={{ ...labelMono, color: GREEN, fontSize: '10px', flexShrink: 0 }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(33,131,51,0.25)' }} />
      </Box>
      {description && (
        <Typography sx={{
          fontSize: '0.85rem', fontWeight: 400,
          color: 'rgba(0,0,0,0.60)', lineHeight: 1.6,
          maxWidth: 520, ...bodyFont,
        }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

// ─── HomePanel ────────────────────────────────────────────────────────────────

function HomePanel({ onSelectTool }) {
  // Attach onSelect to each tool so ToolCard can call it
  const briefTools  = BRIEF_TOOLS.map((t) => ({ ...t, onSelect: onSelectTool }));
  const actionTools = ACTION_TOOLS.map((t) => ({ ...t, onSelect: onSelectTool }));

  return (
    <Box sx={{ height: '100%' }}>

      <Box sx={{
        backgroundColor: '#fff',
        backgroundImage: `
          linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)
        `,
        backgroundSize: '32px 32px',
      }}>
        <Box sx={{
          maxWidth: 960, mx: 'auto',
          px: { xs: 4, md: 5 }, pt: { xs: 5, md: 6 }, pb: { xs: 5, md: 10 },
        }}>

          {/* Welcome header */}
          <Box sx={{ mb: 5 }}>
            <Typography component="h1" sx={{
              fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
              fontWeight: 300, lineHeight: 1.1, letterSpacing: '-0.03em',
              color: CHARCOAL, ...bodyFont, mb: 1.25,
            }}>
              Your renovation,{' '}
              <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 300, color: GREEN }}>
                taking shape.
              </Box>
            </Typography>
            <Typography sx={{ fontSize: '0.9375rem', color: 'rgba(0,0,0,0.68)', lineHeight: 1.6, ...bodyFont }}>
              Use the tools below to build your brief — then let us do the heavy lifting.
            </Typography>
          </Box>

          {/* ── Zone 1: Build Your Brief ── */}
          <ZoneHeader label="BUILD YOUR BRIEF" />

          <Box sx={{
            display: 'grid', pb: { xs: 5, md: 6 },
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}>
            {briefTools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </Box>

          {/* ── Zone 2: From Plan to Reality ── */}
          <ZoneHeader
            label="FROM PLAN TO REALITY"
            description="These tools turn your brief into something real, with samples delivered to your door and quotes you can actually compare."
          />

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}>
            {actionTools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </Box>

        </Box>
      </Box>
    </Box>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
const refinedScrollbar = {
  scrollbarWidth: 'thin',                    // Firefox
  scrollbarColor: `${BORDER} transparent`,   // Firefox: thumb, track
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,0.14)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(0,0,0,0.24)',
  },
};

export default function Dashboard() {
  const [activeTool, setActiveTool] = useState(null);

  return (
    <Box sx={{
      height: '100vh', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', ...bodyFont,
      ...appGridBg,
    }}>

      {/* ── Top bar ── */}
      <Box
        component="header"
        sx={{
          backgroundColor: '#fff',
          borderBottom: `1px solid ${BORDER}`,
          flexShrink: 0, height: 60,
          display: 'flex',
          position: 'relative', zIndex: 40,
        }}
      >
        {/* Left part: same width as the sidebar, so the logo lines up with it */}
        <Box sx={{
          width: 220, flexShrink: 0,
          px: 2.5,
          display: 'flex', alignItems: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Logo: goes back to the Overview page */}
            <Box
              component="button"
              type="button"
              onClick={() => setActiveTool(null)}
              title="Go to Overview"
              sx={{
                background: 'none', border: 'none', cursor: 'pointer', p: 0,
                '&:focus': { outline: 'none' },
                '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '4px' },
              }}
            >
              <Typography component="span" sx={{ ...labelMono, color: CHARCOAL, whiteSpace: 'nowrap' }}>
                Savvy Renovator
              </Typography>
            </Box>
            <Box
              component="span"
              title="We're still improving things. Thanks for trying us early!"
              sx={{
                ...labelMono,
                fontSize: '8px', lineHeight: 1,
                color: GREEN,
                border: '1px solid rgba(33,131,51,0.35)',
                backgroundColor: 'rgba(33,131,51,0.06)',
                px: 0.75, py: '3px',
                whiteSpace: 'nowrap',
              }}
            >
              Beta
            </Box>
          </Box>
        </Box>

        {/* Right part: project name at the far right */}
        <Box sx={{
          flex: 1, minWidth: 0,
          px: { xs: 2, md: 3 },
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        }}>
          <ProjectNameField />
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Sidebar ── */}
        <Box
          component="nav"
          aria-label="Dashboard"
          sx={{
            width: 220, flexShrink: 0,
            backgroundColor: '#fff',
            borderRight: `1px solid ${BORDER}`,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Overview */}
          <Box
            component="button"
            type="button"
            onClick={() => setActiveTool(null)}
            aria-current={activeTool === null ? 'page' : undefined}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2.5, py: 2,
              background: 'none', border: 'none', cursor: 'pointer',
              borderLeft: `2px solid ${activeTool === null ? GREEN : 'transparent'}`,
              borderBottom: `1px solid ${BORDER}`,
              backgroundColor: activeTool === null ? 'rgba(33,131,51,0.06)' : '#fff',
              '&:hover': { backgroundColor: activeTool === null ? 'rgba(33,131,51,0.06)' : STONE50 },
              transition: 'background-color 0.15s ease',
              textAlign: 'left', width: '100%',
              ...focusStyles,
            }}
          >
            <GridViewOutlinedIcon sx={{ fontSize: '1.05rem', color: activeTool === null ? GREEN : CHARCOAL, opacity: activeTool === null ? 1 : 0.55, flexShrink: 0 }} />
            <Typography component="span" sx={{ ...labelMono, fontSize: '10px', color: activeTool === null ? GREEN : CHARCOAL, opacity: activeTool === null ? 1 : 0.75 }}>
              Overview
            </Typography>
          </Box>

          {/* Brief tools */}
          <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
            <Typography sx={{ ...labelMono, color: 'rgba(33,131,51,0.50)', fontSize: '9px' }}>BUILD YOUR BRIEF</Typography>
          </Box>
          {BRIEF_TOOLS.map((tool) => (
            <SidebarItem
              key={tool.id}
              tool={tool}
              isActive={activeTool === tool.id}
              onClick={() => setActiveTool(tool.id)}
            />
          ))}

          {/* Action tools */}
          <Box sx={{ px: 2.5, pt: 2.5, pb: 1, mt: 0.5, borderTop: `1px solid ${BORDER}` }}>
            <Typography sx={{ ...labelMono, color: 'rgba(33,131,51,0.50)', fontSize: '9px' }}>FROM PLAN TO REALITY</Typography>
          </Box>
          {ACTION_TOOLS.map((tool) => (
            <SidebarItem
              key={tool.id}
              tool={tool}
              isActive={activeTool === tool.id}
              onClick={() => setActiveTool(tool.id)}
            />
          ))}

          {/* Footer: account menu + version */}
          <Box sx={{
            mt: 'auto',
            borderTop: `1px solid ${BORDER}`,
            px: 2.5, py: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <AccountPopover
              onNavigate={setActiveTool}
              isActive={activeTool === 'AccountPage'}
            />
            <Typography sx={{ ...labelMono, color: MUTED_FAINT, fontSize: '8px' }}>
              Beta · {APP_VERSION}
            </Typography>
          </Box>
        </Box>

        {/* ── Content area ── */}
        <Box component="main" sx={{ flex: 1, overflowY: 'auto', backgroundColor: 'transparent', ...refinedScrollbar }}>
          {activeTool === null
            ? <HomePanel onSelectTool={setActiveTool} />
            : renderContent(activeTool, setActiveTool)
          }
        </Box>

      </Box>
    </Box>
  );
}
